/**
 * BillingNotificationBell — the header bell for web-billing (Phase 09 / 22, OPEN-085 follow-up).
 *
 * Before this, web-billing had no notifications UI at all, so a BILLING_MANAGER never saw the
 * one notice written for them: catalog_change_apply_approval's "Well X changed operators …
 * Review any open invoices referencing <old operator>" (relatedEntityType 'CATALOG_CHANGE').
 * PR #604 opened GET /v1/notifications to every role, combined roles via their components.
 *
 * The bell and its read logic are web-dispatch's, shared through
 * @lodex-pro/web-shared/notifications. This file supplies what is billing-only:
 *   - Links. A CATALOG_CHANGE row opens the Invoices list — the id is a catalog change, which
 *     has no page here, and the notice asks the reader to review open invoices. Nothing writes
 *     an invoice-scoped notification today, so no row links to a single invoice. A 'job' row
 *     (an InReview escalation, which a TENANT_ADMIN using this app also receives) has no page
 *     in web-billing either, so it only marks read.
 *   - Times in the user's own timezone (DEC-004 / DEC-066).
 * There are no browser-local notices in billing, so the bell shows the server rows only.
 *
 * Read state is shared by the whole role (Corbin, 2026-10-12: keep it for now); the drawer says so.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  NotificationBell,
  useMarkNotificationRead,
  useServerNotifications,
  type InAppNotification,
  type NotificationLink,
} from '@lodex-pro/web-shared/notifications';
import { formatTimestamp } from '../../lib/timestamp';
import { useUserTimezone } from '../../lib/use-user-timezone';

export function BillingNotificationBell(): React.JSX.Element {
  const { data } = useServerNotifications();
  const markRead = useMarkNotificationRead();
  const tz = useUserTimezone();
  const navigate = useNavigate();

  const linkFor = (n: InAppNotification): NotificationLink | null => {
    // OPEN-146 (0231) — an operator renamed, merged or deactivated by Paelode changes the
    // customer name invoices show, like a 0178 operator change.
    if (
      n.relatedEntityType === 'CATALOG_CHANGE' ||
      n.relatedEntityType?.startsWith('CATALOG_OPERATOR_') === true ||
      n.relatedEntityType === 'CATALOG_SUMMARY'
    ) {
      return {
        label: 'Review invoices',
        open: (): void => {
          void navigate('/invoices');
        },
      };
    }
    return null;
  };

  return (
    <NotificationBell
      rows={data?.notifications}
      onMarkRead={(id): void => {
        markRead.mutate(id);
      }}
      formatWhen={(iso): string => formatTimestamp(iso, tz)}
      linkFor={linkFor}
    />
  );
}
